import { useEffect, useState } from "react";
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { arrayMove, SortableContext, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical, ChevronDown, Eye, EyeOff, Save } from "lucide-react";
import { http } from "@/lib/api";
import { toast } from "sonner";
import RichTextEditor from "@/components/RichTextEditor";
import ImageField from "@/components/ImageField";
import VideoField from "@/components/VideoField";

// Editable content schema per section
const SECTION_FIELDS = {
  hero: [
    { key: "hero_video", label: "Hero welcome video (replaces image when set)", type: "video" },
    { key: "hero_video_poster", label: "Video poster image (shown before play)", type: "image" },
    { key: "hero_image", label: "Hero image (used when no video is set)", type: "image" },
    { key: "eyebrow", label: "Eyebrow", type: "text", placeholder: "WELCOME TO" },
    { key: "headline", label: "Headline", type: "multiline", placeholder: "THE\\nSIMPLE\\nTRUTHS\\nCLUB", note: "Use \\n for line breaks" },
    { key: "script", label: "Gold script tagline", type: "text", placeholder: "Sometimes a single reminder can change everything." },
    { key: "body", label: "Body copy", type: "multiline", placeholder: "52 meaningful pieces..." },
    { key: "cta1_label", label: "Primary CTA label", type: "text", placeholder: "Explore the Collection" },
    { key: "cta1_link", label: "Primary CTA link", type: "text", placeholder: "/shop" },
    { key: "cta2_label", label: "Secondary CTA label", type: "text", placeholder: "Join the Club" },
    { key: "cta2_link", label: "Secondary CTA link", type: "text", placeholder: "/subscriptions" },
    { key: "quote", label: "Right-side quote", type: "multiline", placeholder: "Art with a story...\\nTruths you can\\ncarry with you." },
    { key: "signature", label: "Quote signature", type: "text", placeholder: "— Mickey Trivett" },
  ],
  featured: [
    { key: "title", label: "Title", type: "text", placeholder: "FEATURED TRUTHS" },
    { key: "subtitle", label: "Script subtitle", type: "text", placeholder: "Every piece has a meaning. Every story matters." },
  ],
  feature_strip: [
    { key: "f1_title", label: "Feature 1 title", type: "text" },
    { key: "f1_desc", label: "Feature 1 description", type: "text" },
    { key: "f2_title", label: "Feature 2 title", type: "text" },
    { key: "f2_desc", label: "Feature 2 description", type: "text" },
    { key: "f3_title", label: "Feature 3 title", type: "text" },
    { key: "f3_desc", label: "Feature 3 description", type: "text" },
    { key: "f4_title", label: "Feature 4 title", type: "text" },
    { key: "f4_desc", label: "Feature 4 description", type: "text" },
    { key: "f5_title", label: "Feature 5 title", type: "text" },
    { key: "f5_desc", label: "Feature 5 description", type: "text" },
  ],
  two_band: [
    { key: "band1_image", label: "Left band background image", type: "image" },
    { key: "band1_eyebrow", label: "Left eyebrow", type: "text", placeholder: "QR Gallery" },
    { key: "band1_headline", label: "Left headline", type: "multiline", placeholder: "ART IN THE\\nREAL WORLD" },
    { key: "band1_body", label: "Left body", type: "multiline" },
    { key: "band1_cta_label", label: "Left CTA label", type: "text", placeholder: "Learn More" },
    { key: "band1_cta_link", label: "Left CTA link", type: "text", placeholder: "/mobile-gallery" },
    { key: "band2_image", label: "Right band background image", type: "image" },
    { key: "band2_eyebrow", label: "Right eyebrow", type: "text", placeholder: "Join The Simple Truths Club" },
    { key: "band2_headline", label: "Right headline", type: "multiline", placeholder: "GET NEW TRUTHS.\\nDELIVERED." },
    { key: "band2_body", label: "Right body", type: "multiline" },
  ],
  stats: [
    { key: "s1_label", label: "Stat 1 label", type: "text", placeholder: "Collecting Truths" },
    { key: "s1_sub", label: "Stat 1 sub-label", type: "text", placeholder: "Members" },
    { key: "s2_label", label: "Stat 2 label", type: "text", placeholder: "Artworks Released" },
    { key: "s2_sub", label: "Stat 2 sub-label", type: "text", placeholder: "And Growing" },
    { key: "s3_label", label: "Stat 3 label", type: "text", placeholder: "QR Scans" },
    { key: "s3_sub", label: "Stat 3 sub-label", type: "text", placeholder: "And Counting" },
    { key: "s4_label", label: "Stat 4 label", type: "text", placeholder: "Pieces Sold" },
    { key: "s4_sub", label: "Stat 4 sub-label", type: "text", placeholder: "Thank You" },
  ],
  founding52: [
    { key: "eyebrow", label: "Eyebrow", type: "text", placeholder: "Founding 52" },
    { key: "title", label: "Section title", type: "text", placeholder: "THE FOUNDING COLLECTORS" },
    { key: "subtitle", label: "Gold script subtitle", type: "text", placeholder: "The first 52 to carry a truth." },
    { key: "footer", label: "Footer note", type: "text", placeholder: "Be one of the first 52. Your name belongs here." },
  ],
};

function SortableSection({ section, onToggle, content, onContentSave, expanded, onExpandToggle }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: section.key });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };
  const fields = SECTION_FIELDS[section.key] || [];
  const [local, setLocal] = useState(content || {});
  const [saving, setSaving] = useState(false);

  useEffect(() => { setLocal(content || {}); }, [content]);

  const save = async () => {
    setSaving(true);
    try { await onContentSave(section.key, local); }
    finally { setSaving(false); }
  };

  return (
    <div ref={setNodeRef} style={style} data-testid={`section-${section.key}`} className="border border-white/10 bg-[#0a0a0a]">
      <div className="flex items-center gap-3 px-4 py-3">
        <button {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing text-[#8a857d] hover:text-[#C9A055]" data-testid={`drag-${section.key}`}>
          <GripVertical size={18}/>
        </button>
        <button onClick={() => onToggle(section, !section.enabled)} className={`p-1 ${section.enabled !== false ? "text-emerald-400" : "text-[#8a857d]"}`} data-testid={`toggle-${section.key}`}>
          {section.enabled !== false ? <Eye size={16}/> : <EyeOff size={16}/>}
        </button>
        <span className="font-ui-tight text-[#F4F1EA] flex-1">{section.label}</span>
        <span className="font-ui text-[10px] tracking-[0.22em] text-[#8a857d]">#{section.sort_order}</span>
        {fields.length > 0 && (
          <button onClick={() => onExpandToggle(section.key)} className="text-[#C9A055] hover:text-[#E0BC72]" data-testid={`expand-${section.key}`}>
            <ChevronDown size={16} className={`transition-transform ${expanded ? "rotate-180" : ""}`}/>
          </button>
        )}
      </div>
      {expanded && fields.length > 0 && (
        <div className="border-t border-white/10 p-4 space-y-3 bg-black/30">
          <div className="grid sm:grid-cols-2 gap-3">
            {fields.map(f => (
              <div key={f.key} className={(f.type === "multiline" || f.type === "richtext" || f.type === "image" || f.type === "video") ? "sm:col-span-2" : ""}>
                <div className="font-ui text-[9px] tracking-[0.32em] text-[#8a857d] mb-1">{f.label}</div>
                {f.type === "image" ? (
                  <ImageField
                    value={local[f.key] ?? ""}
                    onChange={(v) => setLocal({...local, [f.key]: v})}
                    testid={`${section.key}-${f.key}`}
                  />
                ) : f.type === "video" ? (
                  <VideoField
                    value={local[f.key] ?? ""}
                    onChange={(v) => setLocal({...local, [f.key]: v})}
                    testid={`${section.key}-${f.key}`}
                  />
                ) : f.type === "richtext" ? (
                  <RichTextEditor
                    value={local[f.key] ?? ""}
                    onChange={(v) => setLocal({...local, [f.key]: v})}
                    placeholder={f.placeholder}
                    testid={`field-${section.key}-${f.key}`}
                  />
                ) : f.type === "multiline" ? (
                  <textarea
                    rows={3}
                    value={local[f.key] ?? ""}
                    onChange={e => setLocal({...local, [f.key]: e.target.value})}
                    placeholder={f.placeholder}
                    className="stc-input font-body text-sm"
                    data-testid={`field-${section.key}-${f.key}`}
                  />
                ) : (
                  <input
                    value={local[f.key] ?? ""}
                    onChange={e => setLocal({...local, [f.key]: e.target.value})}
                    placeholder={f.placeholder}
                    className="stc-input text-sm"
                    data-testid={`field-${section.key}-${f.key}`}
                  />
                )}
                {f.note && <div className="font-ui text-[9px] text-[#8a857d] mt-1">{f.note}</div>}
              </div>
            ))}
          </div>
          <div className="flex justify-end pt-2 border-t border-white/5">
            <button disabled={saving} onClick={save} className="btn-gold text-[10px]" data-testid={`save-${section.key}`}>
              <Save size={12}/> {saving ? "Saving…" : "Save Content"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function SectionsBlockEditor() {
  const [sections, setSections] = useState([]);
  const [contentMap, setContentMap] = useState({});
  const [expanded, setExpanded] = useState({});
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const load = async () => {
    const [{ data: secs }, { data: content }] = await Promise.all([
      http.get("/admin/sections"),
      http.get("/admin/content"),
    ]);
    setSections(secs);
    const map = {};
    for (const c of content) map[c.key] = c.value;
    setContentMap(map);
  };

  useEffect(() => { load(); }, []);

  const onDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIdx = sections.findIndex(s => s.key === active.id);
    const newIdx = sections.findIndex(s => s.key === over.id);
    const next = arrayMove(sections, oldIdx, newIdx).map((s, i) => ({ ...s, sort_order: i + 1 }));
    setSections(next);
    await http.put("/admin/sections", { sections: next });
    toast.success("Order saved.");
  };

  const toggleEnabled = async (sec, enabled) => {
    const next = sections.map(s => s.key === sec.key ? { ...s, enabled } : s);
    setSections(next);
    await http.put("/admin/sections", { sections: next });
  };

  const saveContent = async (key, value) => {
    await http.put(`/admin/content/section_${key}`, { key: `section_${key}`, value });
    setContentMap(m => ({...m, [`section_${key}`]: value}));
    toast.success("Content saved.");
  };

  return (
    <div className="space-y-3">
      <div className="font-ui text-[10px] tracking-[0.32em] text-[#C9A055] mb-2">Drag to reorder · Click eye to toggle · Click chevron to edit content</div>
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
        <SortableContext items={sections.map(s => s.key)} strategy={verticalListSortingStrategy}>
          <div className="space-y-2">
            {sections.map(s => (
              <SortableSection
                key={s.key}
                section={s}
                onToggle={toggleEnabled}
                content={contentMap[`section_${s.key}`]}
                onContentSave={saveContent}
                expanded={!!expanded[s.key]}
                onExpandToggle={(k) => setExpanded(e => ({...e, [k]: !e[k]}))}
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>
    </div>
  );
}
